import { useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import ReactSelect, {
  GroupBase,
  OnChangeValue,
  Props as ReactSelectProps,
} from "react-select";

import { useTheme } from "@/modules/theme/hooks/useTheme";

import { DisplayErrorMessage } from "../ErrorMessage/DisplayErrorMessage";
import { InputErrorMessageProps } from "../ErrorMessage/InputErrorMessage";
import { InputError } from "../InputError/InputError";
import { Label } from "../Label/Label";

import {
  createReactSelectStyles,
  createSelectInputStyles,
} from "./helpers/createSelectInputStyles";

export type SelectOptionValue = string | number;

export type BasicOption = {
  value: SelectOptionValue;
  label?: SelectOptionValue;
};

export type SelectInputProps = {
  name: string;
  label?: React.ReactNode;
  onChange: (value: SelectOptionValue | SelectOptionValue[]) => void;
  isSearchable?: boolean;
  showErrorMessage?: boolean;
  loading?: boolean;
} & InputErrorMessageProps;

export function SelectInput<
  Option extends BasicOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  label,
  isSearchable = false,
  showErrorMessage = true,
  loading = false,
  error,
  errorMessages,
  className,
  styles,
  ErrorMessage,
  onErrorChange,
  onChange,
  ...props
}: Omit<ReactSelectProps<Option, IsMulti, Group>, "onChange"> &
  SelectInputProps) {
  const theme = useTheme();
  const classes = createSelectInputStyles(theme);

  const customStyles =
    styles ??
    createReactSelectStyles<Option, IsMulti, Group>({
      theme,
      state: {
        error: Boolean(error),
      },
    });

  /*
  The function below handles the `onChange` event in React Select component.
  It accepts a parameter called `selectValues` which is the type:

  OnChangeValue<Option, IsMulti extends boolean> = 
  IsMulti extends true 
      ? MultiValue<Option> 
      : SingleValue<Option>;

  As you can see, it can be MultiValue<Option> or SingleValue<Option> depending
  on the IsMulti value:

  SingleValue<Option> = Option | null;
  MultiValue<Option> = readonly Option[];

  SingleValue<Option> can be null in the types, so we have to check this condition
  at the beginning.

  By checking if "value" exists in the `selectedValues` we create a simple "type guard"
  so TypeScript compiler knows which type is the `selectedValues` at the time.

  Unfortunately value returned by `ref` is in the

    {
      value: string;
      label: string;
    }
  
  format (when `SingleValue` or an array of the values, when `MultiValue`),
  so we have to set the value of a field manually using `setValue`.

  It's a common case, that select component libraries don't share a `ref` or return
  a value in different format than expected.
  */

  const handleChange = useCallback(
    (selectedValues: OnChangeValue<Option, IsMulti>) => {
      if (!selectedValues) {
        return;
      }

      const value =
        // this is a "type guard" that check if a `selectedValues`
        // is a SingleValue<Option> or MultiValue<Option>
        "value" in selectedValues
          ? selectedValues.value
          : selectedValues.map((option) => option.value);

      onChange(value);
    },
    [onChange]
  );

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={name} className={classes.label}>
          {label}
        </Label>
      )}
      {loading ? (
        <Skeleton height={40} />
      ) : (
        <ReactSelect
          {...props}
          styles={customStyles}
          isSearchable={isSearchable}
          onChange={handleChange}
        />
      )}
      {showErrorMessage && (
        <InputError className={classes.error}>
          <DisplayErrorMessage
            error={error}
            errorMessages={errorMessages}
            ErrorMessage={ErrorMessage}
            onErrorChange={onErrorChange}
          />
        </InputError>
      )}
    </div>
  );
}
